import type { WhisperVariant } from '@core/types';
import { DEFAULT_STT_DEVICE, type SttDevice } from './engines/stt';

const DEVICE_KEY = 'ru-stt-device';
const VARIANT_KEY = 'ru-stt-variant';
const WARMUP_KEY = 'ru-stt-warmup';

/**
 * Engine choices live in localStorage rather than React state so the translator
 * and the diagnostics screen agree without either owning the other, and so a
 * setting survives the tab being killed — which on this project happens often
 * enough to matter.
 */
/**
 * Always `wasm` now, and a stored `webgpu` is migrated rather than honoured.
 *
 * The WebGPU execution provider is no longer in the bundle at all — the build
 * aliases onnxruntime-web to its WASM-only entry, because the JSEP build that
 * carries WebGPU crashes Safari (see `vite.config.ts`). Returning `webgpu` here
 * would ask for a backend that does not exist and fail at pipeline creation
 * with an error naming none of this.
 */
export function getSttDevice(): SttDevice {
  try {
    const v = localStorage.getItem(DEVICE_KEY);
    return v === 'wasm' ? v : DEFAULT_STT_DEVICE;
  } catch {
    return DEFAULT_STT_DEVICE;
  }
}

export function setSttDevice(device: SttDevice) {
  try {
    localStorage.setItem(DEVICE_KEY, device);
  } catch {
    /* private mode */
  }
}

/**
 * `tiny` was offered and then withdrawn.
 *
 * It is genuinely faster and it is genuinely too weak at Russian to be worth it
 * — tested side by side against base on the same sentences, it was not better
 * in any condition, and Russian is the entire point of this app. A speed knob
 * that costs the thing you came for is not a knob, it is a trap.
 *
 * The migration is deliberate rather than incidental. Anyone who selected tiny
 * before it was withdrawn has `tiny` sitting in localStorage, and reading it
 * back would pin them to a model the UI no longer offers and cannot switch away
 * from. Anything that is not a model we still ship resolves to base.
 */
export function getSttVariant(): WhisperVariant {
  try {
    const v = localStorage.getItem(VARIANT_KEY);
    return v === 'base' || v === 'small' ? v : 'base';
  } catch {
    return 'base';
  }
}

export function setSttVariant(variant: WhisperVariant) {
  try {
    localStorage.setItem(VARIANT_KEY, variant);
  } catch {
    /* private mode */
  }
}

/** Last measured warm-up duration, shown so a slow first run is explicable. */
export function getLastWarmUpMs(): number | null {
  try {
    const v = localStorage.getItem(WARMUP_KEY);
    return v === null ? null : Number(v);
  } catch {
    return null;
  }
}

export function setLastWarmUpMs(ms: number) {
  try {
    localStorage.setItem(WARMUP_KEY, String(Math.round(ms)));
  } catch {
    /* private mode */
  }
}
